import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { useEditReimbursementVerification } from '../hooks';
import { getDeviceSource } from './utils';
import { requestClaimsResults } from '../actions';
import { MARKETING_CHANNEL_ID } from '../theme/theme.constants';
import { CLAIM_STATUS_ACTION } from '../constants';
import { PAYMENT_ERROR_RESULT } from './mixpanel.constants';

export const EV1 = ({ source, policyBrand = '', pms = '' }) => ({
  eventName: 'view insurance',
  params: { pms, 'policy brand': String(policyBrand).toUpperCase(), source },
});

export const EV2 = ({ source }) => ({
  eventName: 'view claims',
  params: { source },
});

export const EV3 = () => ({
  eventName: 'start new claim',
});

export const EV4 = ({
  claimData,
  claimDescription,
  dateOfLoss,
  lastBotText,
  lastInputFromUser,
  policyNumber,
  policyBrand,
}) => {
  const params = {
    'claim data': claimData,
    'claim description': claimDescription,
    'date of loss': dateOfLoss,
    'last bot text': lastBotText,
    'last input from user': lastInputFromUser,
    'policy brand': String(policyBrand).toUpperCase(),
    'policy number': policyNumber,
  };

  return {
    eventName: 'abandon claim',
    params,
  };
};

export const EV5 = ({
  attachmentInfo = [{ filename: '', size: '' }],
  claimData,
  claimResult,
  pms,
  policyBrand,
  policyNumber,
  reimbursementMethod,
  timeSpentBeforeSubmit,
}) => {
  const params = {
    'attachment info': attachmentInfo,
    'claim data': claimData,
    'claim result': claimResult,
    pms,
    'policy brand': String(policyBrand).toUpperCase(),
    'policy number': policyNumber,
    'reimbursement method': reimbursementMethod,
    'time spent before submit': timeSpentBeforeSubmit,
  };

  return {
    eventName: 'submit claim',
    params,
  };
};

export const EV6 = ({
  claimNumber,
  claimStatusLabel,
  petName,
  paidAmount,
  pms,
  policyBrand,
  policyNumber,
}) => {
  const params = {
    'claim number': claimNumber,
    'claim status': claimStatusLabel,
    'paid amount': paidAmount,
    'pet name': petName,
    pms,
    'policy brand': String(policyBrand).toUpperCase(),
    'policy number': policyNumber,
  };

  return {
    eventName: 'view claim detail',
    params,
  };
};

export const EV7 = ({ claimNumber, numberOfAttachments, policyBrand }) => ({
  eventName: 'view claim attachments',
  params: {
    'claim number': claimNumber,
    'number of attachments': numberOfAttachments,
    'policy brand': String(policyBrand).toUpperCase(),
  },
});

export const EV8 = ({ claimNumber, fileName, filePath, policyBrand }) => ({
  eventName: 'open claim attachments',
  params: {
    'claim number': claimNumber,
    'file name': fileName,
    'file path': filePath,
    'policy brand': String(policyBrand).toUpperCase(),
  },
});

export const EV9 = () => ({
  eventName: 'view dashboard',
});

export const EV10 = ({
  petDateOfBirth,
  breedId,
  breedName,
  petDescription,
  petId,
  petImageSelected,
  petName,
  petSpecies,
  petWeight,
  petGender,
}) => {
  const params = {
    'pet breed id': breedId,
    'pet breed name': breedName,
    'pet description': petDescription,
    'pet dob': petDateOfBirth,
    'pet gender': petGender,
    'pet id': petId,
    'pet image selected': petImageSelected,
    'pet name': petName,
    'pet species': petSpecies,
    'pet weight': petWeight,
  };

  return {
    eventName: 'add pet',
    params,
  };
};

export const EV11 = ({ petId }) => ({
  eventName: 'delete pet',
  params: { petId },
});

export const EV15 = ({
  customerEmail,
  customerGuid,
  customerName,
  followStatus,
  ownPet,
  petId,
  petOwnerId,
}) => {
  const params = {
    'customer guid': customerGuid,
    'own pet': ownPet,
    'target customer email': customerEmail || '',
    'target customer follow status': followStatus,
    'target customer name': customerName || '',
    'target pet id': petId,
    'target pet owner id': petOwnerId || '',
  };

  return {
    eventName: 'view pet profile',
    params,
  };
};

export const EV39 = ({ source, uploadSource }) => ({
  eventName: 'add file to inbox and docs',
  params: {
    source,
    'source of upload': uploadSource,
  },
});

export const EV40 = ({ fileName }) => ({
  eventName: 'open file',
  params: { 'file name': fileName },
});

export const EV53 = ({
  customerId,
  petId,
  policyBrand,
  source,
}) => ({
  eventName: 'start quote',
  params: {
    'customer id': customerId,
    'pet id': petId,
    'policy brand': String(policyBrand).toUpperCase(),
    source,
  },
});

export const EV54 = ({
  date,
  policyBrand,
  policyNumber,
}) => ({
  eventName: 'renewal cancelation request',
  params: {
    date,
    'policy brand': String(policyBrand).toUpperCase(),
    'policy number': policyNumber,
    source: getDeviceSource(),
  },
});

export const EV55 = ({
  date,
  policyBrand,
  policyNumber,
}) => ({
  eventName: 'continue with the coverage',
  params: {
    date,
    'policy brand': String(policyBrand).toUpperCase(),
    'policy number': policyNumber,
    source: getDeviceSource(),
  },
});

export const EV56 = ({
  date,
  policyBrand,
  policyNumber,
}) => ({
  eventName: 'change coverage',
  params: {
    date,
    'policy brand': String(policyBrand).toUpperCase(),
    'policy number': policyNumber,
    source: getDeviceSource(),
  },
});

export const EV57 = ({
  date,
  policyBrand,
  policyNumber,
}) => ({
  eventName: 'change coverage confirmation',
  params: {
    date,
    'policy brand': String(policyBrand).toUpperCase(),
    'policy number': policyNumber,
    source: getDeviceSource(),
  },
});

export const MIXPANEL_LOGIN_EVENTS = {
  b2cStarted: 'login b2c flow started',
  b2cSuccess: 'login b2c success',
  error: 'login error',
  getApiTokens: 'login getting api\'s tokens',
  getPersonalInfo: 'login getting personalInfo',
  getPersonalInfoError: 'login getting personalInfo error',
  getPersonalInfoSuccess: 'login getting personalInfo success',
  getTerms: 'login getting terms',
  getTermsSuccess: 'login getting terms success',
  invalidCustomerId: 'invalid petCloudCustomerId',
  sendLog: 'login log sent',
};

/**
 * Register an login error
 * @param {Object} param0 - event data
 * @param {String} params0.mixpanelEvent - Event name,
 *  see {@link MIXPANEL_LOGIN_EVENTS}
 * @param {String} email - Event email register
 * @param {String} error - Event error register
 */
export const EV_LOGIN = ({
  mixpanelEvent = '',
  email = '',
  error = '',
}) => ({
  eventName: mixpanelEvent,
  params: { email, error },
});

export const EV_RECAPTCHA = ({ token, error }) => ({
  eventName: 'reCaptchaValidator - MyPetCloud',
  params: { error, 'reCAPTCHA token': token },
});

export const EV_RECAPTCHA_ERROR = ({
  message,
  path,
  request,
  response,
  status,
}) => ({
  eventName: 'reCaptchaError - MyPetCloud',
  params: {
    message,
    path,
    request,
    response,
    status,
  },
});

export const useEditReimbursementEvent = () => {
  const dispatch = useDispatch();
  const store = useSelector(({ claims }) => ({ claims }));
  const { claimsResults, loading } = store.claims;

  const hasOpenClaims = useMemo(() => {
    if (!claimsResults.open || !claimsResults.open.items) {
      return false;
    }

    return claimsResults.open.items.length > 0;
  }, [claimsResults]);

  const editReimbursementVerificationFlg = useEditReimbursementVerification();
  const data = editReimbursementVerificationFlg ? {
    'has open claims': hasOpenClaims,
  } : {};

  const EV58 = ({ source }) => ({
    eventName: 'edit reimbursement method',
    params: { source, ...data },
  });

  const SOURCES = {
    insurance: 'insurance',
    myAccount: 'my account',
  };

  useEffect(() => {
    dispatch(
      // load open claims for mixpanel event
      requestClaimsResults({
        marketChannelIds: [MARKETING_CHANNEL_ID.figo],
        pageNumber: 0,
        petId: null,
        status: CLAIM_STATUS_ACTION.OPEN,
      }),
    );
  }, []);

  return {
    EV58,
    loadingClaims: loading,
    SOURCES,
  };
};

export const EV59 = ({ data = {} }) => ({
  eventName: 'update reimbursement method',
  params: { ...data },
});

export const EV60 = ({
  brandName = '',
  entryPoint = '',
  marketChannelId = -1,
  paymentProvider = '',
}) => ({
  eventName: 'update payment method viewed',
  params: {
    'Entry Point': entryPoint,
    'Market Channel': brandName,
    MarketChannelId: marketChannelId,
    'Payment Provider': paymentProvider,
  },
});

export const EV61 = ({
  brandName = '',
  entryPoint = '',
  marketChannelId = -1,
  paymentProvider = '',
}) => ({
  eventName: 'update payment method started',
  params: {
    'Entry Point': entryPoint,
    'Market Channel': brandName,
    MarketChannelId: marketChannelId,
    'Payment Provider': paymentProvider,
  },
});

export const EV62 = ({
  brandName = '',
  entryPoint = '',
  errorCode = '',
  marketChannelId = -1,
  paymentProvider = '',
  result = PAYMENT_ERROR_RESULT.success,
}) => ({
  eventName: 'update payment method submitted',
  params: {
    'Entry Point': entryPoint,
    errorCode,
    'Market Channel': brandName,
    MarketChannelId: marketChannelId,
    'Payment Provider': paymentProvider,
    result,
  },
});

export const EV63 = ({
  brandName = '',
  entryPoint = '',
  marketChannelId = -1,
  paymentProvider = '',
}) => ({
  eventName: 'update payment method flow cancelled',
  params: {
    'Entry Point': entryPoint,
    'Market Channel': brandName,
    MarketChannelId: marketChannelId,
    'Payment Provider': paymentProvider,
  },
});

export const EV64 = ({
  brandName = '',
  entryPoint = '',
  errorCode = '',
  marketChannelId = -1,
  paymentProvider = '',
}) => ({
  eventName: 'update payment method webview failed to load',
  params: {
    'Entry Point': entryPoint,
    errorCode,
    'Market Channel': brandName,
    MarketChannelId: marketChannelId,
    'Payment Provider': paymentProvider,
  },
});
